import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { screenCopy, skillIncomeExamples } from "@/constants/Brand"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import {
  CertificatePathway,
  ContactSkillTrend,
  CourseRecommendation,
  NetworkContact,
  domains,
  getContactsForSkillWithTrends,
  getCourseForSkill,
  getDomain,
  getPathwayForSkill,
  getSkill,
} from "@/data/prooftree"
import { getEqProofTreeProgress } from "@/services/prooftreeBackend"
import {
  buildProofTreeImmersion,
  isSkillLockedPreview,
  isSkillUnlocked,
} from "@/services/prooftreeImmersion"
import { router, useLocalSearchParams } from "expo-router"
import {
  Award,
  Banknote,
  BookOpenCheck,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Clock3,
  Lock,
  Radio,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UsersRound,
} from "lucide-react-native"
import { ReactNode, useEffect, useRef, useState } from "react"
import { Animated, Easing, Platform, TouchableOpacity, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

export default function SkillDetailScreen() {
  const auth = useAuth()
  const { id, locked } = useLocalSearchParams<{ id: string; locked?: string }>()
  const skill = getSkill(id)
  const domain = getDomain(skill.domainId)
  const color = domain?.color ?? Colors.dark.primary
  const currentLevel = Math.min(skill.completedLevels + 1, skill.levels.length)
  const course = getCourseForSkill(skill.id)
  const pathway = getPathwayForSkill(skill.id)
  const bestContacts = getContactsForSkillWithTrends(skill.id, 3)
  const scrollY = useRef(new Animated.Value(0)).current
  const [treeDomains, setTreeDomains] = useState(domains)
  const immersionState = buildProofTreeImmersion(treeDomains)
  const isLockedByProgress = !isSkillUnlocked(skill.id, immersionState)
  const isLockedPreview = locked === "1" || isSkillLockedPreview(skill.id, immersionState)
  const skillLocked = isLockedByProgress || isLockedPreview
  const [expandedLevels, setExpandedLevels] = useState(() =>
    skill.levels.filter((level) => level.status === "In Progress").map((level) => level.id),
  )

  const toggleLevel = (levelId: string) => {
    setExpandedLevels((current) =>
      current.includes(levelId)
        ? current.filter((currentLevelId) => currentLevelId !== levelId)
        : [...current, levelId],
    )
  }

  useEffect(() => {
    let cancelled = false

    async function loadTreeProgress() {
      if (!auth.session) {
        setTreeDomains(domains)
        return
      }

      const progress = await getEqProofTreeProgress()
      if (!cancelled) setTreeDomains(progress.domains)
    }

    loadTreeProgress()

    return () => {
      cancelled = true
    }
  }, [auth.session?.access_token, auth.session])

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow={domain?.title ?? "ProofTree Skill"}
        title={skill.title}
        subtitle={`${screenCopy.skill.subtitlePrefix} • Level ${currentLevel}/${skill.levels.length}`}
        accentColor={color}
        backFallbackHref="/tree"
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 196 : 180,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="mb-5 rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.skill}1A`,
            borderColor: `${Colors.dark.skill}52`,
          }}
        >
          <Text className="mb-2 text-lg font-bold text-white">{screenCopy.skill.incomeTitle}</Text>
          <Text className="text-base leading-6" style={{ color: Colors.dark.textPrimary }}>
            {skill.whyItMatters}
          </Text>
        </Box>

        {skillLocked ? (
          <Box
            className="mb-5 rounded-3xl border p-5"
            style={{
              backgroundColor: `${color}12`,
              borderColor: `${color}55`,
            }}
          >
            <HStack className="items-start gap-3">
              <Lock color={color} size={20} />
              <VStack className="flex-1 gap-1">
                <Text className="font-bold text-white">Coming next in the ProofTree</Text>
                <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                  Practice the currently unlocked skills in this domain to open this skill. You can
                  preview the behaviors here, but Coach Mode and proof logging stay locked.
                </Text>
              </VStack>
            </HStack>
          </Box>
        ) : null}

        <RecommendedCourseCard course={course} color={color} />
        <SkillPathwayCard pathway={pathway} color={color} />

        <Box
          className="mb-5 rounded-3xl border p-5"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: `${color}55`,
          }}
        >
          <HStack className="items-start gap-4">
            <Text className="text-4xl">{skill.icon}</Text>
            <VStack className="flex-1 gap-2">
              <Text className="text-lg font-bold text-white">Skill focus</Text>
              <Text className="leading-6" style={{ color: Colors.dark.textSecondary }}>
                {skill.description}
              </Text>
            </VStack>
          </HStack>
        </Box>

        <Box
          className="mb-5 rounded-3xl border p-5"
          style={{
            backgroundColor: `${color}12`,
            borderColor: `${color}44`,
          }}
        >
          <HStack className="mb-3 items-center gap-2">
            <Banknote color={color} size={20} />
            <Text className="text-lg font-bold text-white">Opportunity signals</Text>
          </HStack>
          <VStack className="gap-3">
            {skillIncomeExamples.map((example, index) => (
              <HStack key={example} className="items-start gap-3">
                <Box
                  className="mt-0.5 h-6 w-6 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${color}24` }}
                >
                  <Text className="text-xs font-bold" style={{ color }}>
                    {index + 1}
                  </Text>
                </Box>
                <Text className="flex-1 leading-5" style={{ color: Colors.dark.textSecondary }}>
                  {example}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        <BestPracticedWith contacts={bestContacts} />

        <Text className="mb-3 text-xl font-bold text-white">Your coach recommends</Text>
        <VStack className="mb-6 gap-3">
          {skill.examples.map((example, index) => (
            <Box
              key={example}
              className="rounded-2xl border p-4"
              style={{
                backgroundColor: Colors.dark.surfaceSecondary,
                borderColor: index === 0 ? `${color}66` : "rgba(255,255,255,0.06)",
              }}
            >
              <HStack className="items-start gap-3">
                <Box
                  className="h-9 w-9 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${color}22` }}
                >
                  {index === 0 ? (
                    <Sparkles color={color} size={17} />
                  ) : (
                    <ClipboardCheck color={color} size={17} />
                  )}
                </Box>
                <VStack className="flex-1 gap-1">
                  <Text className="font-bold text-white">Coach move {index + 1}</Text>
                  <Text className="leading-5" style={{ color: Colors.dark.textSecondary }}>
                    {example}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          ))}
        </VStack>

        <Text className="mb-3 text-xl font-bold text-white">Coaching timeline</Text>
        <VStack className="mb-7">
          {skill.levels.map((level, index) => {
            const active = level.status === "In Progress"
            const completed = level.status === "Completed"
            const locked = level.status === "Locked"
            const expanded = expandedLevels.includes(level.id)

            return (
              <HStack key={level.id} className="items-start">
                <VStack className="mr-3 items-center">
                  <LevelMarker completed={completed}>
                    <View
                      className="h-11 w-11 items-center justify-center rounded-full border"
                      style={{
                        backgroundColor:
                          completed || active ? `${color}22` : Colors.dark.surfaceTertiary,
                        borderColor: completed || active ? `${color}88` : "rgba(255,255,255,0.08)",
                      }}
                    >
                      {completed ? (
                        <CheckCircle2 color={color} size={20} />
                      ) : locked ? (
                        <Lock color={Colors.dark.textSecondary} size={18} />
                      ) : (
                        <Radio color={color} size={18} />
                      )}
                    </View>
                  </LevelMarker>
                  {index < skill.levels.length - 1 ? (
                    <View
                      className="h-24 w-px"
                      style={{ backgroundColor: completed ? `${color}AA` : `${color}33` }}
                    />
                  ) : null}
                </VStack>

                <TouchableOpacity
                  activeOpacity={0.86}
                  className="mb-3 flex-1"
                  onPress={() => toggleLevel(level.id)}
                >
                  <Box
                    className="rounded-3xl border p-5"
                    style={{
                      backgroundColor: active
                        ? `${Colors.dark.primary}1A`
                        : Colors.dark.surfaceSecondary,
                      borderColor: active ? Colors.dark.primary : "rgba(255,255,255,0.07)",
                    }}
                  >
                    <VStack className="gap-3">
                      <HStack className="items-start justify-between gap-3">
                        <VStack className="flex-1">
                          <HStack
                            className="mb-2 self-start items-center gap-1 rounded-full px-2 py-1"
                            style={{ backgroundColor: `${color}20` }}
                          >
                            <Sparkles color={color} size={12} />
                            <Text className="text-[10px] font-bold" style={{ color }}>
                              COACH
                            </Text>
                          </HStack>
                          <Text className="font-bold text-white">{level.title}</Text>
                          <Text
                            className="mt-1 text-xs font-bold"
                            style={{ color: locked ? Colors.dark.textSecondary : color }}
                          >
                            {level.status}
                          </Text>
                        </VStack>
                        <ChevronDown
                          color={locked ? Colors.dark.textSecondary : color}
                          size={18}
                          style={{ transform: [{ rotate: expanded ? "180deg" : "0deg" }] }}
                        />
                      </HStack>
                      <Text className="leading-5" style={{ color: Colors.dark.textSecondary }}>
                        {level.description}
                      </Text>
                      {expanded && level.behaviorIndicators.length > 0 ? (
                        <VStack className="gap-2">
                          {level.behaviorIndicators.map((indicator) => (
                            <HStack key={indicator} className="items-start gap-2">
                              <CheckCircle2 color={color} size={14} />
                              <Text
                                className="flex-1 text-xs leading-4"
                                style={{ color: Colors.dark.textSecondary }}
                              >
                                {indicator}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      ) : null}
                      {expanded && !locked && !skillLocked ? (
                        <TouchableOpacity
                          className="self-start rounded-full border px-4 py-2"
                          style={{
                            backgroundColor: "transparent",
                            borderColor: `${color}66`,
                          }}
                          onPress={() => router.push(`/coach/${skill.id}`)}
                        >
                          <HStack className="items-center gap-2">
                            <Sparkles color={color} size={14} />
                            <Text className="text-sm font-bold" style={{ color }}>
                              Coach this moment
                            </Text>
                          </HStack>
                        </TouchableOpacity>
                      ) : null}
                    </VStack>
                  </Box>
                </TouchableOpacity>
              </HStack>
            )
          })}
        </VStack>
      </Animated.ScrollView>

      <Box
        className="absolute bottom-0 left-0 right-0 border-t px-5 pb-6 pt-4"
        style={{
          backgroundColor: "rgba(10, 20, 20, 0.96)",
          borderColor: `${Colors.dark.primary}2E`,
        }}
      >
        <VStack className="gap-3">
          <Button
            size="xl"
            className="rounded-2xl"
            style={{
              backgroundColor: Colors.dark.primary,
              shadowColor: Colors.dark.primary,
              shadowOpacity: 0.35,
              shadowRadius: 14,
              shadowOffset: { width: 0, height: 8 },
            }}
            onPress={() => (skillLocked ? router.push("/tree") : router.push(`/coach/${skill.id}`))}
          >
            <Sparkles color={Colors.dark.primaryForeground} size={18} />
            <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
              {skillLocked ? "Open ProofTree" : "Coach this moment"}
            </ButtonText>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="rounded-2xl border"
            isDisabled={skillLocked}
            style={{ borderColor: `${Colors.dark.proof}6B`, opacity: skillLocked ? 0.48 : 1 }}
            onPress={() =>
              router.push({
                pathname: "/proof/new",
                params: { skillId: skill.id, level: `Level ${skill.completedLevels + 1}` },
              })
            }
          >
            <Award color={Colors.dark.proof} size={17} />
            <ButtonText className="font-bold" style={{ color: Colors.dark.proof }}>
              Log Proof
            </ButtonText>
          </Button>
        </VStack>
      </Box>
    </SafeAreaView>
  )
}

function BestPracticedWith({
  contacts,
}: {
  contacts: { contact: NetworkContact; trend: ContactSkillTrend }[]
}) {
  if (contacts.length === 0) return null

  return (
    <Box
      className="mb-5 rounded-3xl border p-5"
      style={{
        backgroundColor: `${Colors.dark.growth}12`,
        borderColor: `${Colors.dark.growth}44`,
      }}
    >
      <HStack className="mb-4 items-center gap-2">
        <UsersRound color={Colors.dark.growth} size={20} />
        <Text className="text-lg font-bold text-white">Best practiced with</Text>
      </HStack>
      <VStack className="gap-3">
        {contacts.map(({ contact, trend }) => {
          const trendColor =
            trend.direction === "negative"
              ? Colors.dark.error
              : trend.direction === "positive"
                ? Colors.dark.growth
                : Colors.dark.textSecondary
          const TrendIcon = trend.direction === "negative" ? TrendingDown : TrendingUp

          return (
            <TouchableOpacity
              key={`${contact.id}-${trend.skillId}`}
              activeOpacity={0.86}
              onPress={() =>
                router.push({
                  pathname: "/contact/[id]",
                  params: { id: contact.id },
                })
              }
            >
              <Box
                className="rounded-2xl border p-4"
                style={{
                  backgroundColor: Colors.dark.surfaceSecondary,
                  borderColor: `${trendColor}3D`,
                }}
              >
                <HStack className="items-center gap-3">
                  <Box
                    className="h-10 w-10 items-center justify-center rounded-full"
                    style={{ backgroundColor: `${trendColor}1F` }}
                  >
                    <TrendIcon color={trendColor} size={18} />
                  </Box>
                  <VStack className="flex-1 gap-1">
                    <Text className="font-bold text-white">{contact.name}</Text>
                    <Text className="text-sm" style={{ color: Colors.dark.textSecondary }}>
                      {contact.relationship} · Current score {trend.currentScore}/100
                    </Text>
                  </VStack>
                  <Text className="text-sm font-bold" style={{ color: trendColor }}>
                    {trend.changePercent > 0 ? "+" : ""}
                    {trend.changePercent}%
                  </Text>
                </HStack>
              </Box>
            </TouchableOpacity>
          )
        })}
      </VStack>
    </Box>
  )
}

function RecommendedCourseCard({
  course,
  color,
}: {
  course: CourseRecommendation | undefined
  color: string
}) {
  if (!course) return null

  return (
    <Box
      className="mb-5 rounded-3xl border p-5"
      style={{
        backgroundColor: `${color}12`,
        borderColor: `${color}44`,
      }}
    >
      <HStack className="mb-4 items-start gap-3">
        <Box
          className="h-11 w-11 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${color}22` }}
        >
          <BookOpenCheck color={color} size={20} />
        </Box>
        <VStack className="flex-1 gap-1">
          <Text className="text-xs font-bold" style={{ color }}>
            RECOMMENDED COURSE PREVIEW
          </Text>
          <Text className="text-lg font-bold text-white">{course.title}</Text>
          <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
            {course.provider} · {course.duration}. {course.outcome}
          </Text>
        </VStack>
      </HStack>
      <Box
        className="rounded-2xl border p-3"
        style={{
          backgroundColor: Colors.dark.surfaceSecondary,
          borderColor: `${color}33`,
        }}
      >
        <HStack className="items-center gap-3">
          <Award color={Colors.dark.proof} size={18} />
          <VStack className="flex-1">
            <Text className="text-xs font-bold" style={{ color: Colors.dark.proof }}>
              You can earn
            </Text>
            <Text className="font-bold text-white">{course.certificateTitle}</Text>
          </VStack>
          <Clock3 color={Colors.dark.textSecondary} size={17} />
        </HStack>
      </Box>
    </Box>
  )
}

function SkillPathwayCard({
  pathway,
  color,
}: {
  pathway: CertificatePathway | undefined
  color: string
}) {
  if (!pathway) return null

  return (
    <Box
      className="mb-5 rounded-3xl border p-5"
      style={{
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: `${color}44`,
      }}
    >
      <HStack className="items-start gap-3">
        <Box
          className="h-10 w-10 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${Colors.dark.proof}22` }}
        >
          <Award color={Colors.dark.proof} size={18} />
        </Box>
        <VStack className="flex-1 gap-1">
          <Text className="text-xs font-bold" style={{ color: Colors.dark.proof }}>
            ACHIEVEMENT PATH
          </Text>
          <Text className="font-bold text-white">
            Level {pathway.currentLevel} → Level {pathway.targetLevel}
          </Text>
          <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
            Needs {pathway.proofsRemaining} proofs and {pathway.coursesRemaining} course to unlock{" "}
            {pathway.certificateTitle}. {pathway.streakLabel}.
          </Text>
        </VStack>
      </HStack>
    </Box>
  )
}

function LevelMarker({ children, completed }: { children: ReactNode; completed: boolean }) {
  const scale = useRef(new Animated.Value(1)).current

  useEffect(() => {
    if (!completed) {
      return
    }

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(scale, {
          toValue: 1.06,
          duration: 900,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(scale, {
          toValue: 1,
          duration: 900,
          easing: Easing.in(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    )

    animation.start()
    return () => animation.stop()
  }, [completed, scale])

  return <Animated.View style={{ transform: [{ scale }] }}>{children}</Animated.View>
}
